import * as Yup from 'yup'
import React, { useState, useRef } from 'react'
import { Formik, Form, Field } from 'formik'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
  Box,
  Typography,
  FormHelperText,
  Grid,
  Checkbox,
  FormControlLabel,
  Tooltip
} from '@mui/material'
// import ScanIcon from '@mui/icons-material/Scan';  // Add the barcode scan icon
import DocumentScannerOutlinedIcon from '@mui/icons-material/CropFree'
import { useSelector } from 'react-redux'
import MachineListAPIs from '../../../utilities/api/machine/AdminMachineList'
import CustomLocationDialog from './CustomLocation'
import CustomCategoryDialog from './CustomCategory'
import InventoryLocationAPIs from '../../../utilities/api/location/AdminLocationList'
import InventoryCategoryAPIs from '../../../utilities/api/category/AdminInventoryCategoryList'
import FormField from '../../shared/FormField'
import { toast } from 'react-toastify'
import { KeyboardArrowDown } from '@mui/icons-material'

const availabilityList = ['Moderate', 'Very Low', 'Low', 'High', 'Always']
const statusList = ['Operational', 'Under Maintenance', 'Out of Service']

const validationSchema = Yup.object().shape({
  name: Yup.string().required('Product type is required'),
  upc: Yup.number(),
  instances: Yup.number().required('Instances is required'),
  description: Yup.string(),
  category: Yup.string(),
  location: Yup.string(),
  availability: Yup.string(),
  status: Yup.string(),
  supervised: Yup.boolean(),
  manufacturer: Yup.string(),
  purchase_cost: Yup.number().typeError('A valid number is required'),
  image: Yup.mixed().nullable()
})

// Styling for form fields remains the same
const styledField = {
  maxWidth: '70%',
  padding: '0px 4px 8px 6px',
  '& .MuiFormLabel-root': {
    marginLeft: '4px'
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderRadius: '12px',
    border: '1px solid #8C8C8C'
  }
}
const headerProps = { sx: { fontWeight: '400', color: '#666666' } }

const AddMachineDialog = ({
  open,
  onClose,
  update,
  setUpdate,
  flag,
  setFlag,
  initialData = null,
  setScannerOpen
}) => {
  const user = useSelector((state) => state.user.user)
  const token = user.access

  const [openCustomLocation, setOpenCustomLocation] = useState(false)
  const [customLocation, setCustomLocation] = useState('')
  const [openCustomCategory, setOpenCustomCategory] = useState(false)
  const [customCategory, setCustomCategory] = useState('')
  const [newImage, setNewImage] = useState(null)
  const fileInputRef = useRef(null)

  const category = useSelector((state) => state.category.category_list)
  const location = useSelector((state) => state.location.location_list)
  const categories = category.map((item) => item.name)
  const locations = location.map((item) => item.name)

  const handleConfirmLocation = () => {
    setOpenCustomLocation(false)
    InventoryLocationAPIs.LocationListPost(customLocation, token)
      .then(() => {
        toast.success('Location added successfully')
        setFlag((prev) => !prev)
      })
      .catch((err) => {
        toast.error(`Failed to add location: ${err.response?.data?.message || err.message}`)
      })
  }

  const handleConfirmCategory = () => {
    setOpenCustomCategory(false)
    InventoryCategoryAPIs.CategoryListPost(customCategory, token)
      .then(() => {
        toast.success('Category added successfully')
        setFlag((prev) => !prev)
      })
      .catch((err) => {
        toast.error(`Failed to add category: ${err.response?.data?.message || err.message}`)
      })
  }

  const initialDataValues = {
    name: initialData?.name || '',
    upc: initialData?.upc || '',
    manufacturer: initialData?.manufacturer || '',
    description: initialData?.description || '',
    availability: initialData?.availability || '',
    location: initialData?.location || '',
    instances: initialData?.instances || '',
    status: initialData?.status || '',
    purchase_cost: initialData?.purchase_cost || '',
    image: initialData?.image || '',
    category: initialData?.category || '',
    is_Hidden: initialData?.is_Hidden || false,
    supervised: initialData?.supervised || false
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Add New Machine</Typography>

          {/* Barcode Scanner Icon Button */}
          <Tooltip title="Coming soon">
            <IconButton
              sx={{
                padding: '12px 16px',
                borderRadius: '12px',
                border: '1px solid #E6E6E6',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            // onClick={() => setScannerOpen(true)} // Open the scanner dialog
            >
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 400, fontSize: '16px', color: '#5C5C5C', mr: '8px' }}
              >
                Scan
              </Typography>
              <DocumentScannerOutlinedIcon sx={{ fontSize: '30px' }} />
            </IconButton>
          </Tooltip>
        </Box>
      </DialogTitle>

      <Formik
        initialValues={initialDataValues}
        validationSchema={validationSchema}
        onSubmit={(values, { resetForm }) => {
          const formData = new FormData()

          Object.entries(values).forEach(([key, value]) => {
            formData.append(key, value)
          })

          if (values.image) {
            formData.append('image', values.image)
          }

          formData.append('availability1', availabilityList.indexOf(values.availability).toString())
          formData.append('status1', statusList.indexOf(values.status).toString())

          MachineListAPIs.AddInventory(formData, token)
            .then(() => {
              toast.success('Machine added successfully')
              resetForm()
              setNewImage(null)
              onClose()
              setUpdate((prev) => !prev)
            })
            .catch((error) => {
              const errorMsg = error.response?.data
                ? Object.values(error.response.data).flat().join(' ')
                : 'An unexpected error occurred.'
              toast.error(`Failed to add machine: ${errorMsg}`)
            })
        }}
      >
        {({ handleSubmit, values, touched, errors, handleChange, setFieldValue, resetForm }) => {
          const handleFileChange = (event) => {
            const file = event.currentTarget.files[0]
            if (!file) return

            if (file.size > 1048576) {
              toast.error('File size should not exceed 1MB')
              return
            }

            if (!file.type.startsWith('image/')) {
              toast.error('Only image files are allowed')
              return
            }

            setFieldValue('image', file)
            setNewImage(URL.createObjectURL(file))
          }

          const handleDiscard = () => {
            resetForm()
            setNewImage(null)
            onClose()
            toast.info('Form discarded')
          }

          return (
            <Form onSubmit={handleSubmit} encType="multipart/form-data">
              <DialogContent>
                <Grid container spacing={10}>
                  <Grid item xs={12} sm={6}>
                    <FormField
                      name="name"
                      label="Name"
                      heading="Name*"
                      sx={styledField}
                      headerProps={headerProps}
                    />
                    <FormField
                      name="upc"
                      label="Enter UPC"
                      fieldType="textfield"
                      heading="UPC"
                      sx={styledField}
                      headerProps={headerProps}
                    />
                    <FormField
                      name="instances"
                      fieldType="textfield"
                      heading="Instances*"
                      label="Select Instances"
                      sx={styledField}
                      headerProps={headerProps}
                    />
                    <FormField
                      name="manufacturer"
                      label="Enter Manufacturer Name"
                      fieldType="textfield"
                      heading="Manufacturer"
                      sx={styledField}
                      headerProps={headerProps}
                    />
                    <FormField
                      name="availability"
                      fieldType="dropdown"
                      heading="Availability"
                      label="Select Availability"
                      options={availabilityList}
                      sx={styledField}
                      SelectProps={{
                        IconComponent: KeyboardArrowDown
                      }}
                      headerProps={headerProps}
                    />
                    <FormField
                      name="purchase_cost"
                      label="Enter Purchase Cost"
                      fieldType="textfield"
                      heading="Purchase Cost"
                      sx={styledField}
                      headerProps={headerProps}
                    />

                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 'normal', padding: '5px', color: '#666666' }}
                    >
                      Machine Image
                    </Typography>
                    {newImage && (
                      <img
                        src={newImage}
                        alt="Preview"
                        style={{ width: '200px', height: '200px' }}
                      />
                    )}
                    <Button
                      variant="contained"
                      onClick={() => fileInputRef.current.click()}
                      sx={{
                        height: newImage ? '40px' : '100px',
                        width: '150px',
                        padding: newImage ? '8px 12px' : '12px 16px',
                        margin: 1,
                        background: newImage ? '#FFE6E6' : '#E6F2FF',
                        color: '#3F3F3F',
                        border: newImage ? '1px solid #FF6666' : '1px solid #66B0FF',
                        borderRadius: '8px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center'
                      }}
                    >
                      <Typography sx={{ fontSize: newImage ? 14 : 24, mt: newImage ? 0 : -1 }}>
                        {newImage ? 'Change Image' : '+'}
                      </Typography>
                      {!newImage && (
                        <Typography sx={{ fontSize: 14, fontWeight: 500 }}>Upload File</Typography>
                      )}
                    </Button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      style={{ display: 'none' }}
                    />
                    {touched.image && errors.image && (
                      <FormHelperText error>{errors.image}</FormHelperText>
                    )}
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Box display="flex" flexDirection="column" position="relative">
                      <FormField
                        name="category"
                        fieldType="dropdown"
                        heading="Category"
                        options={categories}
                        sx={styledField}
                        label="Select Category"
                        SelectProps={{
                          IconComponent: KeyboardArrowDown
                        }}
                        addOption
                        handleAddOption={() => setOpenCustomCategory(true)}
                        headerProps={headerProps}
                      />
                    </Box>
                    <CustomCategoryDialog
                      openCustomCategory={openCustomCategory}
                      setCustomCategory={setCustomCategory}
                      onClose={() => setOpenCustomCategory(false)}
                      onConfirm={() => {
                        setFieldValue('category', customCategory)
                        handleConfirmCategory()
                      }}
                    />

                    <Box display="flex" flexDirection="column" position="relative">
                      <FormField
                        name="location"
                        fieldType="dropdown"
                        heading="Location"
                        label="Select Location"
                        options={locations}
                        sx={styledField}
                        SelectProps={{
                          IconComponent: KeyboardArrowDown
                        }}
                        addOption
                        handleAddOption={() => setOpenCustomLocation(true)}
                        headerProps={headerProps}
                      />
                    </Box>
                    <CustomLocationDialog
                      openCustomLocation={openCustomLocation}
                      setCustomLocation={setCustomLocation}
                      onClose={() => setOpenCustomLocation(false)}
                      onConfirm={() => {
                        setFieldValue('location', customLocation)
                        handleConfirmLocation()
                      }}
                    />

                    <FormField
                      name="status"
                      fieldType="dropdown"
                      heading="Status"
                      label="Select Status"
                      options={statusList}
                      sx={styledField}
                      SelectProps={{
                        IconComponent: KeyboardArrowDown
                      }}
                      headerProps={headerProps}
                    />

                    <FormControlLabel
                      control={
                        <Field
                          as={Checkbox}
                          name="supervised"
                          color="primary"
                          checked={values.supervised}
                          onChange={handleChange}
                        />
                      }
                      label={
                        <Typography sx={{ fontSize: '1.25rem', color: '#666666' }}>
                          Supervised
                        </Typography>
                      }
                      labelPlacement="end"
                    />
                    {touched.supervised && errors.supervised && (
                      <FormHelperText error>{errors.supervised}</FormHelperText>
                    )}
                  </Grid>
                </Grid>

                <Typography sx={{ fontSize: '1.2rem', fontWeight: 400, color: '#666666', mt: 4 }}>
                  Description
                </Typography>
                <Field
                  as={TextField}
                  name="description"
                  label="Description"
                  fullWidth
                  multiline
                  rows={4}
                  variant="outlined"
                  value={values.description}
                  onChange={handleChange}
                  sx={{ mt: 2, mb: 1, '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                />
              </DialogContent>

              <DialogActions>
                <Button
                  onClick={handleDiscard}
                  variant="outlined"
                  sx={{ textTransform: 'none', fontWeight: 'bold', borderRadius: '12px', mr: 1 }}
                >
                  Discard
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  sx={{ textTransform: 'none', fontWeight: 'bold', borderRadius: '12px' }}
                >
                  Add Machine
                </Button>
              </DialogActions>
            </Form>
          )
        }}
      </Formik>
    </Dialog>
  )
}

export default AddMachineDialog
